import { Environment } from './Enums';

export interface AppConfig {
  appName: string;
  versionCheckUrl: string;
  updateCheckInterval: number; // in milliseconds
  minimumRequiredVersion: string;
  forceUpdateEnabled: boolean;
  developerMode: boolean;
  versionPopupShortcut: string;
  mobileGestureEnabled: boolean;
  environment: Environment;
}

// Environment-specific configurations
const configs: Record<Environment, AppConfig> = {
  [Environment.DEVELOP]: {
    appName: 'Holifya Experts (Dev)',
    versionCheckUrl: 'http://localhost:3000/version.json',
    updateCheckInterval: 30000, // 30 seconds for development
    minimumRequiredVersion: '0.1.0',
    forceUpdateEnabled: false,
    developerMode: true,
    versionPopupShortcut: 'Shift+V',
    mobileGestureEnabled: true,
    environment: Environment.DEVELOP
  },
  [Environment.STAGE]: {
    appName: 'Holifya Experts (Stage)',
    versionCheckUrl: 'https://stage.holifya.com/version.json',
    updateCheckInterval: 300000, // 5 minutes
    minimumRequiredVersion: '0.1.0',
    forceUpdateEnabled: true,
    developerMode: false,
    versionPopupShortcut: 'Shift+V',
    mobileGestureEnabled: true,
    environment: Environment.STAGE
  },
  [Environment.PRODUCTION]: {
    appName: 'Holifya Experts',
    versionCheckUrl: 'https://app.holifya.com/version.json',
    updateCheckInterval: 300000, // 5 minutes
    minimumRequiredVersion: '0.1.0',
    forceUpdateEnabled: true,
    developerMode: false,
    versionPopupShortcut: 'Shift+V',
    mobileGestureEnabled: true,
    environment: Environment.PRODUCTION
  }
};

/**
 * Get current environment
 */
export function getCurrentEnvironment(): Environment {
  const env = process.env.REACT_APP_ENVIRONMENT as Environment;
  return env && Object.values(Environment).includes(env) ? env : Environment.DEVELOP;
}

/**
 * Get app configuration for current environment
 */
export function getAppConfig(): AppConfig {
  const environment = getCurrentEnvironment();
  return configs[environment];
}

/**
 * Check if app is in development mode
 */
export function isDevelopmentMode(): boolean {
  return getCurrentEnvironment() === Environment.DEVELOP;
}

/**
 * Check if app is in production mode
 */
export function isProductionMode(): boolean {
  return getCurrentEnvironment() === Environment.PRODUCTION;
}

/**
 * Check if force update is enabled
 */
export function isForceUpdateEnabled(): boolean {
  return getAppConfig().forceUpdateEnabled;
}

/**
 * Check if developer mode is enabled
 */
export function isDeveloperMode(): boolean {
  return getAppConfig().developerMode;
}

/**
 * Get version check URL for current environment
 */
export function getVersionCheckUrl(): string {
  return getAppConfig().versionCheckUrl;
}

/**
 * Get update check interval for current environment
 */
export function getUpdateCheckInterval(): number {
  return getAppConfig().updateCheckInterval;
}

/**
 * Get minimum required version for current environment
 */
export function getMinimumRequiredVersion(): string {
  return getAppConfig().minimumRequiredVersion;
}

/**
 * Get app name for current environment
 */
export function getAppName(): string {
  return getAppConfig().appName;
} 